import React from "react";
import {Box, FormGroup, TextField} from "@mui/material";
import {menuItemsInfo} from "../editMenuItems";
import {NodeType} from "../../../../../model/ModelData";
import {FlowSettings} from "./FlowSettings";
import {InputSettings} from "./InputSettings";
import {ConfirmationSettings} from "./ConfirmationSettings";
import {HookSettings} from "./HookSettings";
import {QASettings} from "./QASettings";
import {LLMSettings} from "./LLMSettings";
import {TeachingSettings} from "./TeachingSettings";
import {RecommendationSettings} from "./RecommendationSettings";
import {ChoiceSettings} from "./ChoiceSettings";
import {StartNodeSettings} from "./StartNodeSettings";
import {UserInputSettings} from "./components/user-input-settings";
import {WatchdogSelection} from "./WatchdogSelection";
import {fieldSx} from "../editMenuStyles";
import {SectionHeader, SubSectionHeader, ToggleRow} from "../components/EditMenuSection";
import {SettingsCustomParams} from "../components/SettingsCustomParams";

export const SettingsTab = ({node, setNode, onUpdate, project, nodes}) => {
    const onChangeData = (key, value) => {
        setNode(prev => {
            const state = {...prev};
            state.data[key] = value;
            return state;
        });
    };

    const {primary, secondary} = menuItemsInfo.settings;
    const isInputNode = [NodeType.QUERY, NodeType.TEXT, NodeType.CHOICE].includes(node?.type);

    return (
        <>
            <SectionHeader title={primary} description={secondary}/>

            <TextField
                value={node?.data?.max_ttl || ""}
                size="small"
                type="number"
                label="Max TTL"
                placeholder="Enter number"
                helperText="Maximum attempts to understand user"
                sx={fieldSx}
                onChange={e => onChangeData("max_ttl", e.target.value)}
                onBlur={() => onUpdate(node)}
            />

            <FormGroup>
                <LLMSettings node={node} setNode={setNode} onUpdate={onUpdate} onChangeData={onChangeData}
                             project={project} nodes={nodes}/>
                <ToggleRow
                    label="Enable Data Protection"
                    checked={node?.data?.data_protection}
                    onChange={(event) => onChangeData("data_protection", event.target.checked)}
                    onBlur={() => onUpdate(node)}
                />
                <ToggleRow
                    label="Use Hint"
                    checked={node?.data?.hint}
                    onChange={(event) => onChangeData("hint", event.target.checked)}
                    onBlur={() => onUpdate(node)}
                />
                {node?.type === NodeType.QUERY && (
                    <ToggleRow
                        label="Include Global Intents"
                        checked={node?.data?.global_intents_included}
                        onChange={(event) => onChangeData("global_intents_included", event.target.checked)}
                        onBlur={() => onUpdate(node)}
                    />
                )}
                {isInputNode && (
                    <ToggleRow
                        label="Music on Hold"
                        checked={node?.data?.music_on_hold}
                        onChange={(event) => onChangeData("music_on_hold", event.target.checked)}
                        onBlur={() => onUpdate(node)}
                    />
                )}
            </FormGroup>

            {node?.type === NodeType?.CALL_TRANSFER && (
                <Box>
                    <SubSectionHeader title="Transfer Number"/>
                    <TextField
                        value={node?.data?.transfer_number || ""}
                        size="small"
                        fullWidth
                        placeholder="Enter the number to redirect the call"
                        helperText="Enter the phone number where the call should be transferred."
                        sx={{mt: 0.75, ...fieldSx}}
                        onChange={e => onChangeData("transfer_number", e.target.value)}
                        onBlur={() => onUpdate(node)}
                    />
                </Box>
            )}

            {node?.type === NodeType?.START && (
                <StartNodeSettings node={node} setNode={setNode} onUpdate={onUpdate} onChangeData={onChangeData}
                                   project={project} nodes={nodes}/>
            )}
            {node?.type === NodeType?.TEACHING && (
                <TeachingSettings node={node} setNode={setNode} onUpdate={onUpdate} onChangeData={onChangeData}
                                  project={project} nodes={nodes}/>
            )}
            {node?.type === NodeType?.START_FLOW && (
                <FlowSettings node={node} setNode={setNode} onUpdate={onUpdate} onChangeData={onChangeData}
                              project={project} nodes={nodes}/>
            )}
            {node?.type === NodeType?.QA && (
                <QASettings node={node} setNode={setNode} onUpdate={onUpdate} onChangeData={onChangeData}
                            project={project} nodes={nodes}/>
            )}
            {node?.type === NodeType?.INPUT && (
                <InputSettings node={node} setNode={setNode} onUpdate={onUpdate} onChangeData={onChangeData}
                               project={project} nodes={nodes}/>
            )}
            {node?.type === NodeType?.RECOMMENDATION && (
                <RecommendationSettings node={node} setNode={setNode} onUpdate={onUpdate} onChangeData={onChangeData}
                                        project={project} nodes={nodes}/>
            )}
            {node?.type === NodeType?.CHOICE && (
                <ChoiceSettings node={node} setNode={setNode} onUpdate={onUpdate} onChangeData={onChangeData}
                                project={project} nodes={nodes}/>
            )}
            {node?.type === NodeType?.QUERY && (
                <UserInputSettings node={node} onUpdate={onUpdate} onChangeData={onChangeData}/>
            )}
            {[NodeType.QUERY, NodeType.TEXT, NodeType.TEACHING, NodeType.RECOMMENDATION, NodeType.CHOICE].includes(node?.type) && (
                <ConfirmationSettings node={node} setNode={setNode} project={project} onChangeData={onChangeData}
                                      nodes={nodes} onUpdate={onUpdate}/>
            )}
            {node?.type === NodeType?.HOOK && (
                <HookSettings node={node} setNode={setNode} onUpdate={onUpdate} onChangeData={onChangeData}
                              project={project} nodes={nodes}/>
            )}
            {[NodeType.QUERY, NodeType.INPUT, NodeType.GEN_AI_WORKFLOW, NodeType.RECOMMENDATION, NodeType.QA].includes(node?.type) && (
                <WatchdogSelection node={node} setNode={setNode} onUpdate={onUpdate} onChangeData={onChangeData}
                                   project={project} nodes={nodes}/>
            )}

            <SettingsCustomParams node={node} setNode={setNode} onUpdate={onUpdate}/>
        </>
    );
};
